// Service for handling email notifications via Netlify Functions + RESEND
import AuditService from './auditService'
import * as Sentry from "@sentry/react"

export interface EmailResponse {
  success: boolean
  message?: string
  emailId?: string
  error?: string
  details?: string
}

export interface TrialEmailData {
  email: string
  userName?: string
}

export interface SubscriptionEmailData {
  email: string
  userName?: string
  startedAfterTrial?: boolean
  monthlyPrice?: string
  subscriptionFrequency?: 'weekly' | 'monthly'
  subscriptionPrice?: string
}

export interface PaygStartEmailData {
  email: string
  userName?: string
  startTime: string
  endTime: string
}

export interface CancellationEmailData {
  email: string
  userName?: string
  subscriptionType: 'trial' | 'subscription'
  endDate?: string
}

export class EmailService {
  private baseUrl: string

  constructor() {
    // Utiliser l'URL de base appropriée selon l'environnement
    if (process.env.NODE_ENV === 'production') {
      // En production, utiliser le même domaine pour éviter les problèmes CORS
      this.baseUrl = `${window.location.origin}/.netlify/functions`
    } else {
      this.baseUrl = 'http://localhost:8888/.netlify/functions'
    }
    
    console.log('🔧 EmailService initialized with baseUrl:', this.baseUrl)
  }

  /**
   * Envoie un email de bienvenue lors du début d'essai
   */
  async sendTrialWelcomeEmail(data: TrialEmailData, userId?: string): Promise<EmailResponse> {
    try {
      console.log('📧 Envoi de l\'email de bienvenue d\'essai pour:', data.email)
      console.log('🔗 URL appelée:', `${this.baseUrl}/send-trial-email`)
      console.log('📦 Données envoyées:', data)

      const response = await fetch(`${this.baseUrl}/send-trial-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })

      console.log('📡 Statut de la réponse:', response.status, response.statusText)

      const result = await response.json()
      console.log('📋 Résultat reçu:', result)

      if (response.ok && result.success) {
        console.log('✅ Email de bienvenue d\'essai envoyé avec succès:', result.emailId)

        // Log audit pour chargeback protection
        if (userId) {
          await AuditService.logEmailNotification({
            userId,
            emailType: 'trial_welcome',
            recipientEmail: data.email,
            emailHtml: result.emailContent || 'Trial welcome email sent'
          })
        }

        return {
          success: true,
          message: result.message,
          emailId: result.emailId
        }
      } else {
        console.error('❌ Erreur lors de l\'envoi de l\'email de bienvenue:', result)
        return {
          success: false,
          error: result.error || 'Failed to send trial welcome email',
          details: result.details
        }
      }

    } catch (error) {
      console.error('❌ Erreur réseau lors de l\'envoi de l\'email de bienvenue:', error)
      // Envoyer l'erreur à Sentry pour monitoring complet
      Sentry.captureException(error, {
        tags: {
          service: 'emailService',
          method: 'sendTrialWelcomeEmail'
        },
        extra: {
          recipientEmail: data.email,
          userId,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        error: 'Network error while sending trial welcome email',
        details: error instanceof Error ? error.message : 'Unknown network error'
      }
    }
  }

  /**
   * Envoie un email de bienvenue lors d'un abonnement direct (sans essai)
   */
  async sendSubscriptionWelcomeEmail(data: SubscriptionEmailData, userId?: string): Promise<EmailResponse> {
    try {
      console.log('📧 Envoi de l\'email de bienvenue abonnement direct pour:', data.email)

      const response = await fetch(`${this.baseUrl}/send-subscription-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })

      const result = await response.json()

      if (response.ok && result.success) {
        console.log('✅ Email de bienvenue abonnement direct envoyé avec succès:', result.emailId)

        if (userId) {
          await AuditService.logEmailNotification({
            userId,
            emailType: 'subscription_welcome',
            recipientEmail: data.email,
            emailHtml: result.emailContent || 'Subscription welcome email sent'
          })
        }

        return {
          success: true,
          message: result.message,
          emailId: result.emailId
        }
      } else {
        console.error('❌ Erreur lors de l\'envoi de l\'email abonnement direct:', result)
        return {
          success: false,
          error: result.error || 'Failed to send subscription welcome email',
          details: result.details
        }
      }
    } catch (error) {
      console.error('❌ Erreur réseau lors de l\'envoi de l\'email abonnement direct:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'emailService',
          method: 'sendSubscriptionWelcomeEmail'
        },
        extra: {
          recipientEmail: data.email,
          userId,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        error: 'Network error while sending subscription welcome email',
        details: error instanceof Error ? error.message : 'Unknown network error'
      }
    }
  }

  /**
   * Envoie un email de confirmation de démarrage PAYG
   */
  async sendPaygStartEmail(data: PaygStartEmailData, userId?: string): Promise<EmailResponse> {
    try {
      console.log('📧 Envoi de l\'email de démarrage PAYG pour:', data.email)

      const response = await fetch(`${this.baseUrl}/send-payg-start-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })

      const result = await response.json()

      if (response.ok && result.success) {
        console.log('✅ Email de démarrage PAYG envoyé avec succès:', result.emailId)

        if (userId) {
          await AuditService.logEmailNotification({
            userId,
            emailType: 'payg_start',
            recipientEmail: data.email,
            emailHtml: result.emailContent || 'PAYG start email sent'
          })
        }

        return {
          success: true,
          message: result.message,
          emailId: result.emailId
        }
      } else {
        console.error('❌ Erreur lors de l\'envoi de l\'email PAYG:', result)
        return {
          success: false,
          error: result.error || 'Failed to send PAYG start email',
          details: result.details
        }
      }
    } catch (error) {
      console.error('❌ Erreur réseau lors de l\'envoi de l\'email PAYG:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'emailService',
          method: 'sendPaygStartEmail'
        },
        extra: {
          recipientEmail: data.email,
          userId,
          startTime: data.startTime,
          endTime: data.endTime,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        error: 'Network error while sending PAYG start email',
        details: error instanceof Error ? error.message : 'Unknown network error'
      }
    }
  }

  /**
   * Envoie un email de confirmation d'annulation
   */
  async sendCancellationEmail(data: CancellationEmailData, userId?: string): Promise<EmailResponse> {
    try {
      console.log('📧 Envoi de l\'email d\'annulation pour:', data.email, 'Type:', data.subscriptionType)

      const response = await fetch(`${this.baseUrl}/send-cancellation-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })

      const result = await response.json()

      if (response.ok && result.success) {
        console.log('✅ Email d\'annulation envoyé avec succès:', result.emailId)

        // Log audit pour chargeback protection
        if (userId) {
          await AuditService.logEmailNotification({
            userId,
            emailType: 'cancellation',
            recipientEmail: data.email,
            emailHtml: result.emailContent || `Cancellation email sent for ${data.subscriptionType}`
          })
        }

        return {
          success: true,
          message: result.message,
          emailId: result.emailId
        }
      } else {
        console.error('❌ Erreur lors de l\'envoi de l\'email d\'annulation:', result)
        return {
          success: false,
          error: result.error || 'Failed to send cancellation email',
          details: result.details
        }
      }

    } catch (error) {
      console.error('❌ Erreur réseau lors de l\'envoi de l\'email d\'annulation:', error)
      // Envoyer l'erreur à Sentry pour monitoring complet
      Sentry.captureException(error, {
        tags: {
          service: 'emailService',
          method: 'sendCancellationEmail'
        },
        extra: {
          recipientEmail: data.email,
          subscriptionType: data.subscriptionType,
          userId,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        error: 'Network error while sending cancellation email',
        details: error instanceof Error ? error.message : 'Unknown network error'
      }
    }
  }

  /**
   * Méthode utilitaire pour envoyer un email de bienvenue d'essai de manière asynchrone
   * sans bloquer l'UX en cas d'erreur
   */
  async sendTrialWelcomeEmailAsync(email: string, userName?: string): Promise<void> {
    try {
      console.log('🚀 Début de sendTrialWelcomeEmailAsync pour:', email)
      
      // Envoyer l'email de manière asynchrone
      this.sendTrialWelcomeEmail({ email, userName })
        .then(result => {
          if (result.success) {
            console.log('✅ Email de bienvenue d\'essai envoyé de manière asynchrone')
          } else {
            console.warn('⚠️ Échec de l\'envoi de l\'email de bienvenue (non bloquant):', result.error)
          }
        })
        .catch(error => {
          console.warn('⚠️ Erreur lors de l\'envoi asynchrone de l\'email de bienvenue:', error)
        })
    } catch (error) {
      console.warn('⚠️ Erreur lors de l\'initialisation de l\'envoi asynchrone:', error)
    }
  }

  /**
   * Méthode utilitaire pour envoyer un email de bienvenue abonnement direct de manière asynchrone
   * sans bloquer l'UX en cas d'erreur
   */
  async sendSubscriptionWelcomeEmailAsync(
    email: string,
    userName?: string,
    options?: { startedAfterTrial?: boolean; monthlyPrice?: string; subscriptionFrequency?: 'weekly' | 'monthly'; subscriptionPrice?: string }
  ): Promise<void> {
    try {
      this.sendSubscriptionWelcomeEmail({
        email,
        userName,
        startedAfterTrial: options?.startedAfterTrial,
        monthlyPrice: options?.monthlyPrice,
        subscriptionFrequency: options?.subscriptionFrequency,
        subscriptionPrice: options?.subscriptionPrice
      })
        .then(result => {
          if (result.success) {
            console.log('✅ Email de bienvenue abonnement direct envoyé de manière asynchrone')
          } else {
            console.warn('⚠️ Échec de l\'envoi de l\'email abonnement direct (non bloquant):', result.error)
          }
        })
        .catch(error => {
          console.warn('⚠️ Erreur lors de l\'envoi asynchrone de l\'email abonnement direct:', error)
        })
    } catch (error) {
      console.warn('⚠️ Erreur lors de l\'initialisation de l\'envoi asynchrone abonnement direct:', error)
    }
  }

  /**
   * Méthode utilitaire pour envoyer un email de démarrage PAYG de manière asynchrone
   * sans bloquer l'UX en cas d'erreur
   */
  async sendPaygStartEmailAsync(
    email: string,
    userName: string | undefined,
    startTime: string,
    endTime: string
  ): Promise<void> {
    try {
      this.sendPaygStartEmail({ email, userName, startTime, endTime })
        .then(result => {
          if (result.success) {
            console.log('✅ Email PAYG envoyé de manière asynchrone')
          } else {
            console.warn('⚠️ Échec de l\'envoi de l\'email PAYG (non bloquant):', result.error)
          }
        })
        .catch(error => {
          console.warn('⚠️ Erreur lors de l\'envoi asynchrone de l\'email PAYG:', error)
        })
    } catch (error) {
      console.warn('⚠️ Erreur lors de l\'initialisation de l\'envoi asynchrone PAYG:', error)
    }
  }

  /**
   * Méthode utilitaire pour envoyer un email d'annulation de manière asynchrone
   * sans bloquer l'UX en cas d'erreur
   */
  async sendCancellationEmailAsync(
    email: string, 
    subscriptionType: 'trial' | 'subscription',
    userName?: string,
    endDate?: string
  ): Promise<void> {
    try {
      // Envoyer l'email de manière asynchrone
      this.sendCancellationEmail({ 
        email, 
        userName, 
        subscriptionType, 
        endDate 
      })
        .then(result => {
          if (result.success) {
            console.log('✅ Email d\'annulation envoyé de manière asynchrone')
          } else {
            console.warn('⚠️ Échec de l\'envoi de l\'email d\'annulation (non bloquant):', result.error)
          }
        })
        .catch(error => {
          console.warn('⚠️ Erreur lors de l\'envoi asynchrone de l\'email d\'annulation:', error)
        })
    } catch (error) {
      console.warn('⚠️ Erreur lors de l\'initialisation de l\'envoi asynchrone:', error)
    }
  }

  /**
   * Méthode pour tester la connectivité avec les fonctions Netlify
   */
  async testConnection(): Promise<boolean> {
    try {
      // Test simple avec une requête OPTIONS
      const response = await fetch(`${this.baseUrl}/send-trial-email`, {
        method: 'OPTIONS'
      })
      
      return response.ok
    } catch (error) {
      console.error('❌ Test de connexion échoué:', error)
      // Envoyer l'erreur à Sentry pour monitoring complet
      Sentry.captureException(error, {
        tags: {
          service: 'emailService',
          method: 'testConnection'
        },
        extra: {
          baseUrl: this.baseUrl
        }
      })
      return false
    }
  }
}

// Instance singleton du service email
export const emailService = new EmailService()

// Export par défaut pour faciliter l'importation
export default emailService
